import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import {
  filterRelevantSyncLogs,
  formatImportedOrderAddress,
  formatImportedOrderDateTime,
  formatImportedOrderMoney,
  getImportedOrderConversionMeta,
  getImportedOrderPlatformMeta,
  getImportedOrderQuotes,
  getImportedOrderReadyForPickupMeta,
  getImportedOrderReference,
  getImportedOrderSelectedQuote,
  getImportedOrderStatusMeta,
  getImportedOrderTrackingCode,
  stringifyImportedOrderPayload,
} from '@/utils/importedOrders';

const SnapshotQuoteCard = ({ title, quote }) => {
  const amount = Number(quote?.price_total || 0);
  const currency = quote?.currency || 'FCFA';
  const available = Boolean(quote?.available ?? true);

  return (
    <Card className={available ? 'border-slate-200' : 'border-slate-200 bg-slate-50'}>
      <CardHeader className="pb-3">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-lg">{available ? formatImportedOrderMoney(amount, currency) : 'Non disponible'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-slate-600">
        <div className="flex items-center justify-between">
          <span>Service</span>
          <span className="font-medium text-slate-900">{quote?.service_tier || '-'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Transport</span>
          <span>{formatImportedOrderMoney(quote?.transport_amount, currency)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Express</span>
          <span>{formatImportedOrderMoney(quote?.express_surcharge_amount, currency)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Assurance</span>
          <span>{formatImportedOrderMoney(quote?.insurance_amount, currency)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const ImportedOrderDetailDialog = ({
  open,
  onOpenChange,
  order,
  logs,
  loading,
  converting,
  onConvert,
  onViewDelivery,
  onViewTracking,
}) => {
  const platformMeta = getImportedOrderPlatformMeta(order?.source_platform);
  const statusMeta = getImportedOrderStatusMeta(order?.status);
  const conversionMeta = getImportedOrderConversionMeta(order);
  const readyForPickupMeta = getImportedOrderReadyForPickupMeta(order);
  const selectedQuote = getImportedOrderSelectedQuote(order);
  const quotes = getImportedOrderQuotes(order);
  const trackingCode = getImportedOrderTrackingCode(order);
  const relevantLogs = filterRelevantSyncLogs(logs, order);
  const sourcePayload = stringifyImportedOrderPayload(order?.source_payload);
  const items = Array.isArray(order?.items) ? order.items : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl" data-testid="imported-order-detail-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={platformMeta.className}>{platformMeta.label}</Badge>
            <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
            <Badge className={conversionMeta.className}>{conversionMeta.label}</Badge>
            <Badge className={readyForPickupMeta.className}>{readyForPickupMeta.label}</Badge>
          </div>
          <DialogTitle className="text-2xl">Commande importee {getImportedOrderReference(order)}</DialogTitle>
          <DialogDescription>
            Source de verite backend : snapshots prix/SLA, statut d import et relation vers la livraison Coli Noki.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0">
          <ScrollArea className="h-[min(70vh,calc(100dvh-14rem))] min-h-[240px] pr-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="spinner" />
              </div>
            ) : !order ? (
              <div className="rounded-xl border border-dashed border-slate-200 px-6 py-12 text-center text-slate-500">
                Impossible de charger cette commande importee.
              </div>
            ) : (
              <Tabs defaultValue="overview" className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Vue d&apos;ensemble</TabsTrigger>
                  <TabsTrigger value="snapshots">Snapshots</TabsTrigger>
                  <TabsTrigger value="source">Source & sync</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  <div className="grid gap-4 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Commande externe</CardDescription>
                        <CardTitle className="text-lg">{getImportedOrderReference(order)}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-slate-600">
                        <p>ID source : {order?.external_order_id || '-'}</p>
                        <p>Creee le {formatImportedOrderDateTime(order?.created_at)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Client</CardDescription>
                        <CardTitle className="text-lg">{order?.customer_name || '-'}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-slate-600">
                        <p>{order?.customer_email || 'Email non renseigne'}</p>
                        <p>{order?.customer_phone || 'Telephone non renseigne'}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Livraison Coli Noki</CardDescription>
                        <CardTitle className="text-lg">{order?.delivery_id ? 'Creee' : 'Pas encore creee'}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-slate-600">
                        <p>Delivery ID : {order?.delivery_id || '-'}</p>
                        <p>Tracking : {trackingCode || '-'}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Valeur declaree</CardDescription>
                        <CardTitle className="text-lg">{formatImportedOrderMoney(order?.declared_value, 'FCFA')}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-slate-600">
                        <p>Poids total : {Number(order?.total_weight || 0).toLocaleString('fr-FR')} kg</p>
                        <p>Mode choisi : {order?.selected_delivery_mode || 'standard'}</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Adresses</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 text-sm">
                        <div>
                          <p className="font-medium text-slate-900">Ramassage</p>
                          <p className="text-slate-600">
                            {order?.pickup_address || '-'}{order?.pickup_city ? `, ${order.pickup_city}` : ''}
                          </p>
                          {order?.pickup_reference && (
                            <p className="mt-1 text-slate-500">Référence : {order.pickup_reference}</p>
                          )}
                        </div>
                        <Separator />
                        <div>
                          <p className="font-medium text-slate-900">Livraison</p>
                          <p className="text-slate-600">
                            {order?.delivery_address || '-'}{order?.delivery_city ? `, ${order.delivery_city}` : ''}
                          </p>
                          {(order?.shipping_reference || order?.delivery_reference) && (
                            <p className="mt-1 text-slate-500">
                              Référence : {order.shipping_reference || order.delivery_reference}
                            </p>
                          )}
                        </div>
                        <Separator />
                        <div>
                          <p className="font-medium text-slate-900">Adresse source plateforme</p>
                          <p className="text-slate-600">{formatImportedOrderAddress(order?.shipping_address)}</p>
                        </div>
                        <Separator />
                        <div>
                          <p className="font-medium text-slate-900">Adresse de facturation</p>
                          <p className="text-slate-600">{formatImportedOrderAddress(order?.billing_address)}</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Etat operationnel</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-slate-600">
                        <div className="flex items-center justify-between">
                          <span>Statut imported order</span>
                          <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Conversion delivery</span>
                          <Badge className={conversionMeta.className}>{conversionMeta.label}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Ready for pickup</span>
                          <Badge className={readyForPickupMeta.className}>{readyForPickupMeta.label}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Tracking client</span>
                          <span>{order?.tracking_email_sent_at ? formatImportedOrderDateTime(order.tracking_email_sent_at) : 'Non envoye'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>External order unique key</span>
                          <span className="font-mono text-xs">{order?.external_order_unique_key || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Delivery cible</span>
                          <span>{order?.status_mapping?.delivery_status || '-'}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Produits</CardTitle>
                      <CardDescription>{items.length} ligne(s) importee(s)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {items.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-200 px-6 py-10 text-center text-slate-500">
                          Aucun article detaille n&apos;a ete fourni par la source.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {items.map((item, index) => (
                            <div key={`${item?.sku || item?.title || 'item'}-${index}`} className="rounded-xl border border-slate-200 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <p className="font-medium text-slate-900">{item?.title || item?.name || `Article ${index + 1}`}</p>
                                  <p className="text-sm text-slate-500">{item?.sku || 'SKU non renseigne'}</p>
                                </div>
                                <div className="text-right text-sm text-slate-600">
                                  <p>Qté : {Number(item?.quantity || 1).toLocaleString('fr-FR')}</p>
                                  <p>Poids : {Number(item?.weight || 0).toLocaleString('fr-FR')} kg</p>
                                  <p>Prix : {formatImportedOrderMoney(item?.price, 'FCFA')}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="snapshots" className="space-y-6">
                  <div className="grid gap-4 lg:grid-cols-3">
                    <Card className="lg:col-span-1">
                      <CardHeader>
                        <CardTitle className="text-lg">Snapshot selectionne</CardTitle>
                        <CardDescription>Quote retenu lors de l&apos;import</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-slate-600">
                        <div className="flex items-center justify-between">
                          <span>Service</span>
                          <span className="font-medium text-slate-900">{selectedQuote?.service_tier || order?.selected_delivery_mode || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Total</span>
                          <span className="font-medium text-slate-900">{formatImportedOrderMoney(selectedQuote?.price_total, selectedQuote?.currency || 'FCFA')}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Assurance</span>
                          <span>{Boolean(order?.is_insured) ? 'Oui' : 'Non'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Montant assurance</span>
                          <span>{formatImportedOrderMoney(order?.insurance_amount || selectedQuote?.insurance_amount, selectedQuote?.currency || 'FCFA')}</span>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="lg:col-span-2">
                      <DeliveryEstimateSummary
                        source={order}
                        title="Delai estime snapshot"
                        testId="imported-order-detail-estimate"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <SnapshotQuoteCard title="Quote standard" quote={quotes.standard} />
                    <SnapshotQuoteCard title="Quote express" quote={quotes.express} />
                  </div>
                </TabsContent>

                <TabsContent value="source" className="space-y-6">
                  <div className="grid gap-6 lg:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Payload source</CardTitle>
                        <CardDescription>Charge utile brute conservee cote backend</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          readOnly
                          value={sourcePayload || 'Aucun payload source disponible.'}
                          className="min-h-[320px] font-mono text-xs"
                          data-testid="imported-order-source-payload"
                        />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Etat de sync</CardTitle>
                        <CardDescription>Journal simplifie cote vendeur</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-600">
                          <div className="flex items-center justify-between">
                            <span>Source status</span>
                            <span>{order?.source_status || '-'}</span>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span>Financial status</span>
                            <span>{order?.source_financial_status || '-'}</span>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span>Fulfillment status</span>
                            <span>{order?.source_fulfillment_status || '-'}</span>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span>Mapping cible</span>
                            <span>{order?.status_mapping?.delivery_status || '-'}</span>
                          </div>
                        </div>

                        {relevantLogs.length === 0 ? (
                          <div className="rounded-xl border border-dashed border-slate-200 px-6 py-10 text-center text-slate-500">
                            Aucun log de sync pertinent pour cette commande.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {relevantLogs.slice(0, 8).map((log) => (
                              <div key={log.id} className="rounded-xl border border-slate-200 p-4 text-sm">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="font-medium text-slate-900">{log.event_type}</p>
                                  <Badge className={log.status === 'success' || log.status === 'replayed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                    {log.status}
                                  </Badge>
                                </div>
                                <p className="mt-1 text-slate-500">{formatImportedOrderDateTime(log.created_at)}</p>
                                {log.idempotency_key ? <p className="mt-2 font-mono text-xs text-slate-500">{log.idempotency_key}</p> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {trackingCode ? (
              <Button type="button" variant="outline" onClick={() => onViewTracking?.(order)}>
                Voir le tracking
              </Button>
            ) : null}
            {order?.delivery_id ? (
              <Button type="button" variant="outline" onClick={() => onViewDelivery?.(order)}>
                Voir la livraison
              </Button>
            ) : null}
          </div>
          {!order?.delivery_id ? (
            <Button type="button" className="bg-red-500 hover:bg-red-600" onClick={() => onConvert?.(order)} disabled={converting}>
              {converting ? 'Conversion...' : 'Convertir en livraison'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportedOrderDetailDialog;
